import React, { useEffect, useState } from 'react';
import ProductImage from "../../assets/images/our-product.png";
import EditIcon from "../../assets/svg/edit.svg";
import PlusIcon from "../../assets/svg/plus.svg";
import favoriteIcon from '../../assets/svg/favorite-red.svg';
import DeleteIcon from "../../assets/svg/delete.svg";
import { GetCart, RemoveCartItem, AddToCart, MoveItemToCartOrWishlist } from "../../Services/CartService";
import { getImageUrl, handleImageError } from "../../utils/imageUtils";
import { Link, useNavigate } from "react-router-dom";

const Cart = ({ setCartData }) => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartSummary, setCartSummary] = useState(null);
  // const [cartDataState, setCartDataState] = useState({}); // This state is no longer needed

  useEffect(() => {
    fetchCartItems();
  }, []);

  const fetchCartItems = async () => {
    setLoading(true);
    setError(null);

    try {
      const userId = localStorage.getItem('userId');

      if (!userId) {
        setError('Please login to view cart');
        setLoading(false);
        return;
      }

      const response = await GetCart({
        shoppingCartTypeId: 2,
        userId: parseInt(userId)
      });

      if (response.status && response.data) {
        setCartItems(response.data);

        if (response.data.length > 0 && response.data[0]?.shoppingCartSummary) {
          const summary = response.data[0].shoppingCartSummary;
          setCartSummary(summary);

          const newCartData = {
            amount: summary.totalPrice,
            cartItemIds: response.data.map(item => item.id),
            couponCode: ""
          };
          setCartData(newCartData);
        } else {
          // Handle empty cart
          setCartSummary(null);
          setCartData({});
        }

      } else {
        setError(response.message || 'Failed to load cart items');
      }
    } catch (err) {
      console.error('Error fetching cart items:', err);
      setError('Failed to load cart items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      const response = await RemoveCartItem(itemId);

      if (response.status) {
        // Refresh cart items after successful removal
        fetchCartItems();
        // Dispatch custom event to update cart count in header
        window.dispatchEvent(new Event('cartUpdated'));
      } else {
        setError(response.message || 'Failed to remove item');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      setError('Failed to remove item. Please try again.');
    }
  };

  const handleAddItem = async (item) => {
    try {
      const userId = localStorage.getItem('userId');

      const cartData = {
        productId: item.productId,
        shoppingCartTypeId: 2,
        quantity: 1,
        price: item.price,
        discountPrice: item.discountPrice,
        totalPrice: item.totalPrice,
        weight: item.weight,
        discountId: item.discountId || 0,
        attributesXml: item.attributeXml || "",
        userId: parseInt(userId)
      };

      const response = await AddToCart(cartData);

      if (response.status) {
        // Refresh cart items after successful addition
        fetchCartItems();
        // Dispatch custom event to update cart count in header
        window.dispatchEvent(new Event('cartUpdated'));
      } else {
        setError(response.message || 'Failed to add item');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      setError('Failed to add item. Please try again.');
    }
  };

  const renderCartItem = (item) => {
    const imagePath = getImageUrl(item.path, ProductImage);

    // **FIX:** Clear localStorage before navigating to edit
    const clearCustomisationStorage = () => {
      ['customise-selectedProducts', 'customise-wheatProductId', 'customise-quantities', 'customise-mainProductXml', 'customise-productsXml', 'customise-grindingSelection', 'customise-attributesXml']
        .forEach(key => localStorage.removeItem(key));
    };

    const handleRedirectEdit = (item) => {
      clearCustomisationStorage(); // Clear storage first

      const queryParams = new URLSearchParams({
        type: "edit",
        step: 0,
        force: Date.now() // ← add a unique param to force re-render
      }).toString();

      navigate(
        `/customize-wheat?${queryParams}`,
        { state: { cartItems: item } }
      );
    };

    const handleFavorite = async (itemId) => {
      try {
        const params = {
          cartItemId: parseInt(itemId),
          shoppingCartType: 1, // 1 = cart, 2 = wishlist (assuming)
        };

        const response = await MoveItemToCartOrWishlist(params);

        if (response?.status) {

          await fetchCartItems();

          window.dispatchEvent(new Event('cartUpdated'));
        }
      } catch (error) {
        console.error("Error moving item:", error);
      } finally {
      }
    };

    return (
      <div key={item.id} className="cart-item">
        {/* Product Info */}
        <div className="product-info">
          <div className="product-image">
            <img
              src={getImageUrl(item.path, ProductImage)}
              alt={item.productName}
              onError={(e) => handleImageError(e, ProductImage)}
              className="product-icon"
            />
          </div>

          <div>
            <h5 className="product-name">
              {item.productName} ({item.weight}KG)
            </h5>

            <div className="product-tags">
              {item.attributes?.subProducts?.map((sp) => (
                <span key={sp.id} className="tag">
                  {sp.name} {sp.weight}
                </span>
              ))}
              {item.attributes?.attributes?.map((attr) => (
                <span key={attr.id} className="tag">
                  {attr.value}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Quantity & Price & Delete */}
        <div className={'d-flex align-items-center gap-2 cart-total'}>
          <p className={'fs_20 fw_700 text-black text-no-wrap'}>₹ {Number(item.totalPrice).toFixed(2)}</p>

          <div className={'d-flex align-items-center gap-3'}>

            <div
              className={'action d-flex align-items-center justify-content-center'}
              onClick={() => handleFavorite(item.id)}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={favoriteIcon}
                alt="favorite"
                style={{ width: "16px", height: "16px" }}
              />
            </div>

            {item.isCustomProduct ? <div onClick={() => handleRedirectEdit(item)} style={{ cursor: 'pointer' }} className={'action d-flex align-items-center justify-content-center'}>
              <img className={'img-fluid'} src={EditIcon} alt="edit icon" />
            </div> : null}

            <div
              className={'action d-flex align-items-center justify-content-center'}
              onClick={() => handleAddItem(item)}
              style={{ cursor: 'pointer' }}
            >
              <img className={'img-fluid'} src={PlusIcon} alt="add icon" />
            </div>

            <div
              className={'action d-flex align-items-center justify-content-center'}
              onClick={() => handleRemoveItem(item.id)}
              style={{ cursor: 'pointer' }}
            >
              <img className={'img-fluid'} src={DeleteIcon} alt="delete icon" style={{ width: "16px", height: "16px" }} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='cart-container'>
      <h3 className="cart-title">Your Cart</h3>

      <div className={'cart-main'}>
        {loading ? (
          <div className="text-center py-5">
            <p>Loading cart items...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : cartItems.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">Your cart is empty</p>
          </div>
        ) : (
          <>
            <div className="cart-list">
              {cartItems.map(item => renderCartItem(item))}

              {cartSummary && (
                <div className="cart-summary mt-4 p-3 border-top">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fs_16 fw_500">Wheat Items:</span>
                    <span className="fs_16 fw_600">{cartSummary.wheatItems}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fs_16 fw_500">Subtotal:</span>
                    <span className="fs_16 fw_600">₹ {Number(cartSummary.subTotal).toFixed(2)}</span>
                  </div>
                  {cartSummary.discount > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="fs_16 fw_500 text-success">Discount:</span>
                      <span className="fs_16 fw_600 text-success">- ₹ {cartSummary.discount}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between pt-2 border-top">
                    <span className="fs_18 fw_700">Total:</span>
                    <span className="fs_18 fw_700">₹ {Number(cartSummary.totalPrice).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Cart;
